import React, { useState } from 'react'
import { Container, Spinner, Row, Col, Toast, Nav } from 'react-bootstrap'
import { useGetUsersQuery, useDeleteUserMutation } from '../../../services/userApi'
import { useGetOrganizationPublicQuery } from '../../../services/orgsApi'
import { User } from '../../../types/models/user'
import { useFetchUserProfileQuery } from '../../../services/authApi'
import ConfirmDialog from '../../modals/ConfirmDialog'
import { CreateUserModal, EditUserModal } from './EditUserModal'
import UsersTable from './UsersTable'
import OverviewTab from './components/OverviewTab'
import OrganizationTab from './components/OrganizationTab'
import { TabNavContainer, TabNav, TabContentContainer } from './styles'
import { Users, Building2, BarChart3 } from 'lucide-react'

const AdminDashboard: React.FC = () => {
  const { data: usersData, isLoading: usersLoading, error: usersError } = useGetUsersQuery({})
  const { data: orgData, isLoading: orgLoading, error: orgError } = useGetOrganizationPublicQuery()
  const { data: currentUser } = useFetchUserProfileQuery()

  const [deleteUser] = useDeleteUserMutation()
  const [activeTab, setActiveTab] = useState<string>('overview')

  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [sortConfig, setSortConfig] = useState<{
    key: string
    direction: 'ascending' | 'descending' | null
  }>({ key: '', direction: null })

  const handleEditUser = (user: User) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user)
    setShowDeleteModal(true)
  }

  const handleBulkDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    try {
      if (selectedUser) {
        await deleteUser(selectedUser.id)
        setSelectedUser(null)
      } else {
        for (const userId of selectedUsers) {
          await deleteUser(userId)
        }
        setSelectedUsers([])
      }
      setShowDeleteModal(false)
    } catch (err) {
      setShowError(true)
      setErrorMessage(
        'Failed to delete user(s): ' +
          ((err as unknown as { data?: { message: string } })?.data?.message || 'Unknown Error')
      )
    }
  }

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  const toggleAllUsers = () => {
    const regularUsers = usersData?.users.filter((user) => user.username !== 'admin') || []
    setSelectedUsers((prev) =>
      prev.length === regularUsers.length ? [] : regularUsers.map((user) => user.id)
    )
  }

  const handleSort = (key: string) => {
    let direction: 'ascending' | 'descending' | null = 'ascending'
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') {
        direction = 'descending'
      } else if (sortConfig.direction === 'descending') {
        direction = null
      }
    }
    setSortConfig({ key, direction })
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()

    if (date.getFullYear() < 2000) {
      return 'Never'
    }

    if (date > now) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const diffTime = startOfToday.getTime() - startOfDate.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return (
        date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        }) + ' Today'
      )
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }
  }

  const isLoading = usersLoading || orgLoading
  const error = usersError || orgError

  if (isLoading) {
    return (
      <Container fluid style={{ padding: 0 }}>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '400px' }}
        >
          <Spinner animation="border" style={{ color: '#22d3ee' }} />
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <Container fluid style={{ padding: 0 }}>
        <div className="alert alert-danger mt-4">Error loading data</div>
      </Container>
    )
  }

  const organization = orgData
  const tabs = [
    { key: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
    { key: 'users', label: 'Users', icon: <Users size={18} /> },
    {
      key: 'organization',
      label: 'Organization',
      icon: <Building2 size={18} />,
      disabled: !organization,
    },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          usersData?.users && (
            <OverviewTab
              users={usersData.users}
              currentUser={currentUser}
              formatDate={formatDate}
            />
          )
        )
      case 'users':
        return (
          usersData?.users && (
            <UsersTable
              users={usersData.users}
              selectedUsers={selectedUsers}
              sortConfig={sortConfig}
              onSort={handleSort}
              onToggleUserSelection={toggleUserSelection}
              onToggleAllUsers={toggleAllUsers}
              onEditUser={handleEditUser}
              onDeleteUser={handleDeleteUser}
              onBulkDelete={handleBulkDelete}
              onCreateUser={() => setShowCreateModal(true)}
              formatDate={formatDate}
              organizations={orgData ? [orgData] : []}
            />
          )
        )
      case 'organization':
        return organization ? <OrganizationTab organization={organization} /> : null
      default:
        return null
    }
  }

  return (
    <Container fluid style={{ padding: 0 }}>
      {/* Tab Navigation */}
      <TabNavContainer>
        <TabNav activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
          {tabs.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                eventKey={tab.key}
                disabled={tab.disabled}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {tab.icon}
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </TabNav>
      </TabNavContainer>

      {/* Tab Content */}
      <TabContentContainer>{renderTabContent()}</TabContentContainer>

      {/* Modals */}
      <CreateUserModal show={showCreateModal} onHide={() => setShowCreateModal(false)} />
      <EditUserModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        user={selectedUser}
      />

      <ConfirmDialog
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirmRight={confirmDelete}
        title={selectedUser ? 'Confirm Delete User' : 'Confirm Bulk Delete'}
        message={
          selectedUser ? (
            <>
              Are you sure you want to delete user{' '}
              <strong className="text-danger">{selectedUser?.username}</strong>?
              <p className="text-danger mb-0 mt-3">
                <i className="fas fa-exclamation-triangle me-2"></i>This action cannot be undone.
              </p>
            </>
          ) : (
            <>
              Are you sure you want to delete{' '}
              <strong className="text-danger">{selectedUsers.length} users</strong>?
              <p className="text-danger mb-0 mt-3">
                <i className="fas fa-exclamation-triangle me-2"></i>This action cannot be undone.
              </p>
            </>
          )
        }
        confirmRightText={selectedUser ? 'Delete User' : `Delete ${selectedUsers.length} Users`}
        cancelText="Cancel"
        variant="danger"
      />

      <Toast
        show={showError}
        onClose={() => setShowError(false)}
        className="position-fixed top-0 end-0 m-4"
        style={{ zIndex: 1000 }}
        delay={5000}
        autohide
      >
        <Toast.Header>
          <strong className="me-auto">Error</strong>
        </Toast.Header>
        <Toast.Body>{errorMessage}</Toast.Body>
      </Toast>
    </Container>
  )
}

export default AdminDashboard
